import { useMemo } from "react";
import { decodeConfigurationFromUrl, getConfigParamFromUrl } from "../utils/urlSharing";
import type { BatchPricesResponse } from "../services/pricingService";
import type { ComponentSelections } from "../utils/componentSelections";
import { mapBatchResultsToSelections } from "../utils/componentSelections";
import type { PreResolvedSelectionsResult } from "../types/viewmodel";

export interface UrlConfigurationResult {
	isLoading: boolean;
	isInvalidConfig: boolean;
	invalidConfigMessage: string | null;
	missingProductsCount: number;
	replacedProductGroupIds: string[];
	componentSelections: ComponentSelections | null;
	hasUrlParam: boolean;
}

export function useUrlConfiguration(
	preResolvedConfiguration: PreResolvedSelectionsResult | null | undefined,
	isDataLoaded: boolean,
	bundleId?: string
): UrlConfigurationResult {
	const urlParam = useMemo(() => getConfigParamFromUrl(), []);
	const hasUrlParam = !!urlParam;

	const decodedConfig = useMemo(() => {
		if (!urlParam) return null;
		return decodeConfigurationFromUrl(urlParam);
	}, [urlParam]);

	const hasDecodableConfig = decodedConfig !== null;

	const isBundleIdMismatch = useMemo(() => {
		if (!decodedConfig?.bundleId || !bundleId) return false;
		return decodedConfig.bundleId !== bundleId;
	}, [decodedConfig, bundleId]);

	const preResolvedSelections: BatchPricesResponse | null = preResolvedConfiguration?.selections ?? null;
	const hasBackendError = !!preResolvedConfiguration?.errorMessage;

	const isLoading = hasUrlParam && !isDataLoaded;

	const isInvalidConfig =
		hasUrlParam &&
		isDataLoaded &&
		(isBundleIdMismatch || hasBackendError || !hasDecodableConfig || !preResolvedSelections);

	// Suppress the error message when the config was simply for a different bundle
	const invalidConfigMessage = isBundleIdMismatch
		? null
		: (preResolvedConfiguration?.errorMessage ?? null);

	const componentSelections = useMemo(() => {
		if (preResolvedSelections && hasUrlParam && !isBundleIdMismatch) {
			return mapBatchResultsToSelections(preResolvedSelections);
		}
		return null;
	}, [preResolvedSelections, hasUrlParam, isBundleIdMismatch]);

	const missingProductsCount = useMemo(() => {
		if (preResolvedSelections && hasUrlParam && !isBundleIdMismatch) {
			return preResolvedSelections.missingProductGroups.length;
		}
		return 0;
	}, [preResolvedSelections, hasUrlParam, isBundleIdMismatch]);

	const replacedProductGroupIds = useMemo(() => {
		if (preResolvedSelections && hasUrlParam && !isBundleIdMismatch) {
			const missingGroupIds = preResolvedSelections.missingProductGroups;
			const unavailableGroupIds = preResolvedSelections.results
				.filter((result) => !result.isAvailable)
				.map((result) => result.productGroupId);

			return Array.from(new Set([...missingGroupIds, ...unavailableGroupIds]));
		}
		return [];
	}, [preResolvedSelections, hasUrlParam, isBundleIdMismatch]);

	return {
		isLoading,
		isInvalidConfig,
		invalidConfigMessage,
		missingProductsCount,
		replacedProductGroupIds,
		componentSelections,
		hasUrlParam,
	};
}
